import { Controller, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs/inputText';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { spacing } from 'ui/theme';
import { CustomEventFormStateValues, useCustomEventsQueryContext } from '../contexts';
import { useRetentionPeriodFormData, useUpdateIngestionPolicies } from '../hooks';
import { RetentionPeriodForm } from './RetentionPeriodForm';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: ${spacing.x3};
`;

export const TEXT = {
  ID: 'new-custom-event-modal',
  CONFIRM_BUTTON: 'Create event',
  NAME_ID: 'custom-event-name',
  NAME_LABEL: 'Event name',
  HEADER: 'New custom event',
  RETENTION_PERIOD_ID: 'custom-event-retention-period',
};

type Props = {
  onClose: () => void;
};

export const NewCustomEventModal = ({ onClose }: Props) => {
  const { project } = useCustomEventsQueryContext();
  const {
    formState: { isValid },
    handleSubmit,
  } = useFormContext<CustomEventFormStateValues>();

  const {
    error: formDataError,
    retentionPeriod,
    formData,
    setFormData,
  } = useRetentionPeriodFormData();

  const { add } = useUpdateIngestionPolicies();

  /** Create custom event and provide a defaul retention period */
  const onSuccess = (values: CustomEventFormStateValues) => {
    const { customEventName } = values;

    // Manually check for retention period errors until converted to react-hook-form
    if (!formDataError && isValid) {
      add(customEventName, retentionPeriod, project.id);
    }
  };

  return (
    <Modal
      id={TEXT.ID}
      isModalOpen={true}
      onClose={onClose}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>{TEXT.HEADER}</ModalHeader>
      <ModalBody>
        <Wrapper>
          <Controller
            name="customEventName"
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <FormField
                inputId={TEXT.NAME_ID}
                errorText={fieldState?.error?.message}
                hasError={Boolean(fieldState?.error?.message)}
                labelText={TEXT.NAME_LABEL}
              >
                <InputText
                  {...field}
                  autoFocus
                  id={TEXT.NAME_ID}
                  hasError={Boolean(fieldState?.error?.message)}
                />
              </FormField>
            )}
          />
          <RetentionPeriodForm
            error={formDataError}
            formData={formData}
            id={TEXT.RETENTION_PERIOD_ID}
            setFormData={setFormData}
          />
        </Wrapper>
      </ModalBody>
      <ModalFooter>
        <PrimaryButton onClick={handleSubmit(onSuccess)} size={BUTTON_SIZES.LARGE}>
          {TEXT.CONFIRM_BUTTON}
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
